import { Dispatch } from 'react';
import * as ActionTypes from 'src/constants/ActionTypes';
import { IStateMallPharmacy, IStoreState } from 'src/entity/StateEntity';
import { MallService } from '../../services/MallService';
import { IMallPharmacyDetail, IMallPricePolicy, IMallPharmacySettingInfo, ISettingPharmacySource, ISettingOnlineData } from 'src/entity/MallEntity';
import * as uuid from 'uuid';
import { IDoctorSellerSetting } from 'src/entity/DoctorEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';

export function reloadMallPharmacy(): IStateMallPharmacy {
  return {
    reload: true,
    isFetching: false,
    type: ActionTypes.MALL_PHARMACY_STATUS_RELOAD,
    pharmacyList: [],
  };
}

export function requestMallPharmacy(): IStateMallPharmacy {
  return {
    reload: false,
    isFetching: true,
    type: ActionTypes.MALL_PHARMACY_STATUS_REQUEST,
    pharmacyList: [],
  };
}

function receiveMallPharmacy(pharmacyList: IMallPharmacyDetail[], isSuccess: boolean): IStateMallPharmacy {
  return {
    reload: !isSuccess,
    isFetching: false,
    type: isSuccess ? ActionTypes.MALL_PHARMACY_STATUS_SUCCESS : ActionTypes.MALL_PHARMACY_STATUS_FAILURE,
    pharmacyList,
  };
}

export function fetchMallPharmacy(sellerUserUUID: string): any {
  return async (dispatch: Dispatch<IStateMallPharmacy>) => {
    dispatch(requestMallPharmacy());

    return MallService.searchPharmacy({
      seller_user: sellerUserUUID,
      offset: 0,
      limit: 100000,
    })
      .then((res) => dispatch(receiveMallPharmacy(res.results, true)))
      .catch((error) => dispatch(receiveMallPharmacy([], false)));
  };
}

export function shouldFetchMallPharmacy(mallPharmacyState: IStateMallPharmacy): boolean {
  return (mallPharmacyState.reload || mallPharmacyState.pharmacyList.length === 0) && !mallPharmacyState.isFetching;
}

export function fetchMallPharmacyIfNeeded(): any {
  return (dispatch: Dispatch<IStateMallPharmacy>, getState: any) => {
    const { mallPharmacyState, userState } = getState() as IStoreState;
    if (shouldFetchMallPharmacy(mallPharmacyState) && !!userState.userInfo) {
      return dispatch(fetchMallPharmacy(userState.userInfo.seller_user));
    }
  };
}

/**
 * 通过UUID获取药库明细
 *
 * @export
 * @param {string} pharmacyUUID
 * @returns {(Promise<IMallPharmacyDetail | undefined>)}
 */
export async function fetchPharmacyByUUID(pharmacyUUID: string): Promise<IMallPharmacyDetail | undefined> {
  try {
    if (!!pharmacyUUID) {
      return MallService.getPharmacyByUUID(pharmacyUUID);
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
}

/**
 * 更新药库名称和订单类型
 *
 * @export
 * @param {Partial<IMallPharmacyDetail>} data
 * @returns {(Promise<IMallPharmacyDetail | undefined>)}
 */
export async function changePharmacy(data: Partial<IMallPharmacyDetail>): Promise<IMallPharmacyDetail | undefined> {
  try {
    return MallService.updatePharmacy(data);
  } catch (error) {
    console.error(error);
  }
  return undefined;
}

/**
 * 通过sellerUserUUID获取单元药库列表
 *
 * @export
 * @param {string} sellerUserUUID
 * @returns {Promise<IMallPharmacyDetail[]>}
 */
export async function fetchPharmacyListBySellerUser(sellerUserUUID: string): Promise<IMallPharmacyDetail[]> {
  try {
    const res = await MallService.searchPharmacy({
      seller_user: sellerUserUUID,
      offset: 0,
      limit: 100000,
    });
    if (!!res) {
      return res.results;
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

/**
 * 查询所有的单元药库列表
 *
 * @export
 * @param {string[]} UUIDList
 * @returns {Promise<IMallPharmacyDetail[]>}
 */
export async function fetchAllPharmacyList(UUIDList: string[]): Promise<IMallPharmacyDetail[]> {
  try {
    const res = await MallService.searchPharmacy({
      seller_user__in: UUIDList,
      offset: 0,
      limit: 100000,
    });
    if (!!res) {
      return res.results;
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

/**
 * 通过药库UUID获取价格模式
 *
 * @export
 * @param {string} pharmacyUUID
 * @returns {Promise<IMallPricePolicy[]>}
 */
export async function fetchPricePolicyByPharmacyUUID(pharmacyUUID: string): Promise<IMallPricePolicy[]> {
  try {
    const res = await MallService.getMallPricePolicyByPharmacyUUID(pharmacyUUID);
    if (!!res) {
      return res.results;
    }
  } catch (error) {
    console.error(error);
  }
  return [];
}

/**
 * 药库明细转换为药库设置数据
 *
 * @export
 * @param {IMallPharmacyDetail} detail
 * @param {string} pricePolicy
 * @returns {IMallPharmacySettingInfo}
 */
export function pharmacyDetail2SettingInfo(detail: IMallPharmacyDetail, pricePolicy: string | undefined | null): IMallPharmacySettingInfo {
  return {
    uuid: detail.uuid,
    name: detail.name,
    seller_user: detail.seller_user,
    // dosage_form: detail.dosage_form,
    // brand: detail.brand,
    order_type: detail.order_type,
    medicine_price_policy: pricePolicy,
    category: detail.category
  };
}

export function settingData2OnlineData(
  settingData: {
    dataDrugStoreList: IDoctorSellerSetting[];
    dataPharmacyList: IMallPharmacySettingInfo[];
  },
  drugStoreList: IDrugStoreInfo[]
): ISettingOnlineData[] {
  const result: ISettingOnlineData[] = [];
  for (const obj of settingData.dataDrugStoreList) {
    const drugStoreObj = drugStoreList.find((t) => t.seller_user === obj.seller_user);
    if (!!drugStoreObj) {
      const pharmacySourceList: ISettingPharmacySource[] = [];
      for (const pharmacyObj of settingData.dataPharmacyList) {
        if (pharmacyObj.seller_user === drugStoreObj.seller_user) {
          pharmacySourceList.push({
            uniqueKey: uuid.v4(),
            medicine_group: pharmacyObj.uuid,
            medicine_price_policy: pharmacyObj.medicine_price_policy,
            source: pharmacyObj,
          });
        }
      }
      result.push({
        uniqueKey: uuid.v4(),
        name: drugStoreObj.name,
        seller_user: drugStoreObj.seller_user,
        pharmacySourceList,
      });
    }
  }
  return result;
}

export function cloudSettingInfo2Source(infoList: IMallPharmacySettingInfo[]): ISettingPharmacySource[] {
  const result: ISettingPharmacySource[] = [];
  for (const pharmacyObj of infoList) {
    result.push({
      uniqueKey: uuid.v4(),
      medicine_group: pharmacyObj.uuid,
      medicine_price_policy: pharmacyObj.medicine_price_policy,
      source: pharmacyObj,
    });
  }
  return result;
}

export function localSettingInfo2Source(infoList: IMallPharmacySettingInfo[]): ISettingOnlineData[] {
  const result: ISettingOnlineData[] = [];

  const tempMap = new Map<string, IMallPharmacySettingInfo[]>();
  for (const obj of infoList) {
    if (!!obj.seller_user && !!obj.uuid) {
      const tempValues = tempMap.get(obj.seller_user) || [];
      tempValues.push(obj);
      tempMap.set(obj.seller_user, tempValues);
    }
  }

  tempMap.forEach((list: IMallPharmacySettingInfo[], sellerUUID: string) => {
    let sellerUserName = '';
    const pharmacySourceList: ISettingPharmacySource[] = [];
    for (const obj of list) {
      sellerUserName = obj.seller_user_name || '';
      pharmacySourceList.push({
        uniqueKey: uuid.v4(),
        source: obj,
        /** 单元药库uuid, */
        medicine_group: obj.uuid,
        /** 价格模式uuid */
        medicine_price_policy: obj.medicine_price_policy,
      });
    }
    result.push({
      seller_user: sellerUUID,
      name: sellerUserName,
      uniqueKey: uuid.v4(),
      pharmacySourceList,
    });
  });
  return result;
}

// pharmacySource2Data = (sourceList: ISettingPharmacySource[]): ISettingPharmacyData[] => {
//   const result: ISettingPharmacyData[] = [];
//   for (const sourceObj of sourceList) {
//     result.push({
//       key: sourceObj.key,
//       medicine_group: sourceObj.medicine_group,
//       medicine_price_policy: sourceObj.medicine_price_policy,
//     });
//   }
//   return result;
// };

// pharmacyData2Source = (dataList: ISettingPharmacyData[]): ISettingPharmacySource[] => {
//   const result: ISettingPharmacySource[] = [];
//   for (const sourceObj of dataList) {
//     // result.push({
//     //   key: sourceObj.key,
//     //   medicine_group: sourceObj.medicine_group,
//     //   medicine_price_policy: sourceObj.medicine_price_policy,
//     // });
//   }
//   return result;
// };



// WEBPACK FOOTER //
// ./src/redux/actions/mallPharmacyCreator.ts